/**
 * The HTTP layer shared by every API module: the axios instance, sessions and
 * token refresh, connectivity reporting, cached network-first reads and error
 * messages. Endpoint groups live in api.js (online-only ones) and records.js
 * (field data, which is saved on the device first and synced).
 */
import axios from 'axios';
import { deriveFromSurveys } from './offlineFallback';
import { clearUserData, deleteMeta, readResponse, saveMeta, saveResponse } from './offlineStore';

// In development the CRA dev server proxies /api to Flask. A deployed build is
// served from its own origin, so it is built with the API's full URL instead.
const API_URL = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  // Weak signal can leave a request hanging. Saves get 30 s; reads give up
  // sooner (see get) so pages can fall back to the copy saved on the device.
  timeout: 30000,
});

// ---------------------------------------------------------------------------
// Connectivity
// ---------------------------------------------------------------------------

// No response at all (no signal, server unreachable, timeout), as opposed to
// the server answering with an error.
export const isNetworkError = (error) => Boolean(error) && !error.response && error.code !== 'ERR_CANCELED';

// NetworkContext listens for this. `offline` says whether the API is
// reachable; `savedAt` is set when a page is showing data saved on the device.
export const announce = (detail) => window.dispatchEvent(new CustomEvent('network:status', { detail }));

// The signed-in user, kept on the device so the session survives starting the
// app with no signal, and used to keep each user's saved data separate.
const USER_KEY = 'mangrove:user';
export const cachedUser = {
  get: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY));
    } catch {
      return null;
    }
  },
  set: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
  clear: () => localStorage.removeItem(USER_KEY),
};

// ---------------------------------------------------------------------------
// Session handling
// ---------------------------------------------------------------------------

// A 401 from these endpoints means the credentials themselves were rejected,
// so they must never trigger a token refresh.
const AUTH_URLS = ['/auth/login', '/auth/refresh'];

/**
 * Copy the session into IndexedDB for the service worker, which syncs the
 * outbox while the app is closed and can't read localStorage. Called whenever
 * the tokens change.
 */
export const mirrorSession = () => {
  const user = cachedUser.get();
  const accessToken = localStorage.getItem('access_token');
  if (!user?.id || !accessToken) return deleteMeta('session');
  return saveMeta('session', {
    userId: user.id,
    accessToken,
    refreshToken: localStorage.getItem('refresh_token'),
    apiUrl: new URL(API_URL, window.location.origin).toString().replace(/\/$/, ''),
  });
};

// Drop the stored session and this user's saved data, and tell the app
// (AuthContext listens for this).
export const clearSession = () => {
  const user = cachedUser.get();
  if (user?.id) clearUserData(user.id);
  cachedUser.clear();
  deleteMeta('session');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.dispatchEvent(new Event('auth:logout'));
};

// Exchange the refresh token for a new access token. This uses a bare axios
// call so the interceptors below (which attach the *access* token) are bypassed.
export const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await axios.post(`${API_URL}/auth/refresh`, null, {
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
  const { access_token } = response.data;
  localStorage.setItem('access_token', access_token);
  mirrorSession();
  return access_token;
};

// Concurrent 401s share one in-flight refresh instead of each starting their own.
let refreshPromise = null;

// Attach the access token to every request that doesn't already carry auth.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On an expired access token: refresh once, retry the original request, and
// log the user out if the refresh itself fails. A 403 is announced app-wide so
// the toast layer can explain it without every page handling it.
api.interceptors.response.use(
  (response) => {
    announce({ offline: false });
    return response;
  },
  async (error) => {
    if (isNetworkError(error)) announce({ offline: true });
    const original = error.config;
    const status = error.response?.status;
    const isAuthUrl = AUTH_URLS.some((url) => original?.url?.includes(url));

    if (status === 401 && original && !original._retry && !isAuthUrl) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const token = await refreshPromise;
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch (refreshError) {
        // Only a rejected refresh token ends the session. If the refresh
        // couldn't reach the server, keep the session for when signal returns.
        if (!isNetworkError(refreshError)) clearSession();
        return Promise.reject(isNetworkError(refreshError) ? refreshError : error);
      }
    }

    // An admin set this account's password since it signed in: the app swaps
    // to the choose-a-password screen instead of showing a permission error.
    if (status === 403 && error.response?.data?.errors?.code === 'password_change_required') {
      window.dispatchEvent(new Event('auth:password-change-required'));
    } else if (status === 403) {
      window.dispatchEvent(
        new CustomEvent('api:forbidden', {
          detail: error.response?.data?.message || 'You do not have permission to do that.',
        })
      );
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Every successful API response is wrapped as { status, message, data }.
export const unwrap = (response) => (response.data && 'data' in response.data ? response.data.data : response.data);

const cacheKey = (url, config) => {
  const params = config?.params ? new URLSearchParams(config.params).toString() : '';
  return params ? `${url}?${params}` : url;
};

// Reads go to the network first and keep a copy of every answer on the
// device. With no signal they fall back to that copy, or build it from the
// saved survey list (see offlineFallback.js), and say how old it is.
const READ_TIMEOUT = 10000;

export const get = async (url, config) => {
  const user = cachedUser.get();
  const key = cacheKey(url, config);
  try {
    // The device knows it has no connection: don't wait on the network.
    if (navigator.onLine === false && user?.id) {
      const offline = new Error('Device is offline');
      offline.code = 'ERR_NETWORK';
      throw offline;
    }
    const data = await api.get(url, { timeout: READ_TIMEOUT, ...config }).then(unwrap);
    if (user?.id) saveResponse(user.id, key, data);
    return data;
  } catch (error) {
    if (!isNetworkError(error) || !user?.id) throw error;
    if (error.message === 'Device is offline') announce({ offline: true });
    const saved = await readResponse(user.id, key);
    if (saved) {
      announce({ offline: true, savedAt: saved.savedAt });
      return saved.data;
    }
    const list = await readResponse(user.id, '/surveys');
    const derived = list ? deriveFromSurveys(url, list.data) : undefined;
    if (derived !== undefined) {
      announce({ offline: true, savedAt: list.savedAt });
      return derived;
    }
    throw error;
  }
};
export const post = (url, body) => api.post(url, body).then(unwrap);
export const put = (url, body) => api.put(url, body).then(unwrap);
export const del = (url) => api.delete(url).then(unwrap);

// Fields the API sets itself, or nested relations that must not be sent back
// on an update.
export const READ_ONLY_FIELDS = ['id', 'created_at', 'updated_at', 'creator_id', 'created_by', 'client_id', 'version'];
export const stripForUpdate = (data, nested = []) =>
  Object.fromEntries(
    Object.entries(data).filter(([key]) => !READ_ONLY_FIELDS.includes(key) && !nested.includes(key))
  );

// A readable message from any failed request.
export const getErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (isNetworkError(error)) {
    return error.config?.method && error.config.method !== 'get'
      ? "You're offline, so this can't be saved yet. Try again when you have signal."
      : "You're offline and this hasn't been saved on this device yet.";
  }
  const data = error?.response?.data;
  if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    // Marshmallow validation errors: { field: ["message"] }
    const fieldErrors = Object.entries(data.errors)
      .filter(([, messages]) => Array.isArray(messages))
      .map(([field, messages]) => `${field.replace(/_/g, ' ')}: ${messages.join(', ')}`);
    if (fieldErrors.length) return fieldErrors.join('; ');
  }
  return data?.message || data?.msg || fallback;
};
